import { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Car, FileText, Package, ClipboardList, Sparkles } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useBusiness } from '../../contexts/BusinessContext';
import { useAppSystem } from '../../hooks/useAppSystem';
import { Customer } from '../../lib/customers';
import { getStockItems, StockItem } from '../../lib/stockItems';
import toast from 'react-hot-toast';
import { customerHasOpenOpportunity } from '../../lib/crm/opportunityLinking';
import { createLeadForCustomer, emitLeadCreated } from '../../lib/crm/createLead';
import CreateBookingModal from '../CreateBookingModal';
import CreateMobileBookingModal from '../CreateMobileBookingModal';
import CreateProposalModal from '../CreateProposalModal';
import PlaceOrderModal from '../PlaceOrderModal';
import ScheduleFieldQuoteModal from '../ScheduleFieldQuoteModal';
import CrmCreateActionDropdown, { type CrmCreateActionOption } from '../crm/CrmCreateActionDropdown';

type CreateType = 'booking' | 'mobileBooking' | 'proposal' | 'placeOrder' | 'fieldQuote';

interface CustomerCreateMenuProps {
  /** The customer the create actions will be associated with. If null, the dropdown is hidden. */
  customer: Customer | null;
  /**
   * Optional callback fired immediately before a create modal is opened.
   * Useful when this dropdown lives inside another modal that should close out of the way
   * (e.g. CustomerMessageModal closes itself so the booking modal can take the screen).
   */
  onBeforeOpen?: (type: CreateType) => void;
  /** Dropdown menu alignment. Defaults to `right`. */
  align?: 'left' | 'right';
}

/**
 * Reusable blue "+" dropdown for creating bookings / mobile bookings / proposals / place-orders
 * tied to a specific customer. Visually matches the CRM opportunity-details create dropdown.
 *
 * The component owns:
 *  - the dropdown trigger and menu (via CrmCreateActionDropdown)
 *  - the four downstream create modals (booking / mobile booking / proposal / place order)
 *  - settings + app-installation gating for which menu options are visible
 *
 * It's safe to drop into any header — no z-index plumbing required because the dropdown is an
 * absolutely-positioned popover (not a portal modal) and the downstream modals each manage
 * their own stacking via the shared ModalStack context.
 */
export default function CustomerCreateMenu({
  customer,
  onBeforeOpen,
  align = 'right',
}: CustomerCreateMenuProps) {
  const { selectedBusiness } = useBusiness();
  const { coreApps, installedAddons } = useAppSystem();

  const [activeCreateType, setActiveCreateType] = useState<CreateType | null>(null);
  const [placeOrderItems, setPlaceOrderItems] = useState<StockItem[]>([]);

  // Settings-driven booking-type visibility (same source as the global FAB menu)
  const [showLocationBooking, setShowLocationBooking] = useState(true);
  const [showMobileBooking, setShowMobileBooking] = useState(true);
  const [showFieldQuotes, setShowFieldQuotes] = useState(false);

  const isCalendarAppInstalled = useMemo(
    () => coreApps.some((app) => app.app?.slug === 'calendar')
      || installedAddons.some((app) => app.app?.slug === 'calendar'),
    [coreApps, installedAddons],
  );
  const isProposalsAppInstalled = useMemo(
    () => coreApps.some((app) => app.app?.slug === 'proposals')
      || installedAddons.some((app) => app.app?.slug === 'proposals'),
    [coreApps, installedAddons],
  );
  const isInventoryAppInstalled = useMemo(
    () => coreApps.some((app) => app.app?.slug === 'inventory')
      || installedAddons.some((app) => app.app?.slug === 'inventory'),
    [coreApps, installedAddons],
  );
  const isCrmAppInstalled = useMemo(
    () => coreApps.some((app) => app.app?.slug === 'crm')
      || installedAddons.some((app) => app.app?.slug === 'crm'),
    [coreApps, installedAddons],
  );

  // Whether this customer already has an open CRM lead. `null` while unresolved
  // so we don't flash the "Create Lead" option for an existing lead.
  const [customerHasOpenLead, setCustomerHasOpenLead] = useState<boolean | null>(null);
  const [creatingLead, setCreatingLead] = useState(false);

  const recheckOpenLead = useCallback(async () => {
    if (!isCrmAppInstalled || !selectedBusiness?.id || !customer?.id || customer.is_supplier) {
      setCustomerHasOpenLead(null);
      return;
    }
    try {
      const hasOpen = await customerHasOpenOpportunity(selectedBusiness.id, customer.id);
      setCustomerHasOpenLead(hasOpen);
    } catch (err) {
      console.error('CustomerCreateMenu: failed to check open lead', err);
      setCustomerHasOpenLead(null);
    }
  }, [isCrmAppInstalled, selectedBusiness?.id, customer?.id, customer?.is_supplier]);

  useEffect(() => {
    let cancelled = false;
    setCustomerHasOpenLead(null);
    void (async () => {
      if (cancelled) return;
      await recheckOpenLead();
    })();
    return () => { cancelled = true; };
  }, [recheckOpenLead]);

  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;

    if (!isCalendarAppInstalled) {
      setShowLocationBooking(false);
      setShowMobileBooking(false);
      setShowFieldQuotes(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data: locationData, error: locationError } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'show_location_booking_in_fab',
            p_default_value: true,
          });
        if (!cancelled) {
          setShowLocationBooking(!locationError && locationData !== null ? locationData : true);
        }

        const { data: mobileData, error: mobileError } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'show_mobile_booking_in_fab',
            p_default_value: true,
          });
        if (!cancelled) {
          setShowMobileBooking(!mobileError && mobileData !== null ? mobileData : true);
        }

        const { data: fqData, error: fqError } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'show_field_quotes_in_fab',
            p_default_value: false,
          });
        if (!cancelled) {
          setShowFieldQuotes(!fqError && fqData !== null ? !!fqData : false);
        }
      } catch (err) {
        console.error('CustomerCreateMenu: failed to load calendar settings', err);
      }
    })();

    return () => { cancelled = true; };
  }, [selectedBusiness?.id, isCalendarAppInstalled]);

  const openCreate = async (type: CreateType) => {
    if (type === 'placeOrder' && selectedBusiness?.id && customer?.id) {
      try {
        const items = await getStockItems(selectedBusiness.id);
        setPlaceOrderItems(items);
      } catch (err) {
        console.error('CustomerCreateMenu: failed to load stock items', err);
        return;
      }
    }
    onBeforeOpen?.(type);
    setActiveCreateType(type);
  };

  const handleCreateLead = useCallback(async () => {
    if (!selectedBusiness?.id || !customer?.id || creatingLead) return;
    setCreatingLead(true);
    // Optimistically hide the option so it can't be tapped twice.
    setCustomerHasOpenLead(true);
    try {
      await createLeadForCustomer(selectedBusiness.id, customer);
      emitLeadCreated(customer.id);
      toast.success('Lead created');
    } catch (err) {
      console.error('CustomerCreateMenu: failed to create lead', err);
      const message = err instanceof Error ? err.message : '';
      if (message === 'no-pipeline' || message === 'no-stage') {
        toast.error('Set up a CRM pipeline before creating leads');
      } else {
        toast.error('Could not create lead');
      }
      // Re-check actual state since creation failed.
      void recheckOpenLead();
    } finally {
      setCreatingLead(false);
    }
  }, [selectedBusiness?.id, customer, creatingLead, recheckOpenLead]);

  const menuOptions = useMemo((): CrmCreateActionOption[] => {
    const isSupplier = Boolean(customer?.is_supplier);
    const showOnlyPlaceOrder = isSupplier && isInventoryAppInstalled;

    if (showOnlyPlaceOrder) {
      return [{
        id: 'place-order',
        label: 'Place Order',
        description: 'Create a new purchase order with this supplier',
        icon: Package,
        onSelect: () => openCreate('placeOrder'),
      }];
    }

    const opts: CrmCreateActionOption[] = [];
    if (showLocationBooking) {
      opts.push({
        id: 'location-booking',
        label: 'Location Booking',
        description: 'Create a new location booking',
        icon: Building2,
        onSelect: () => openCreate('booking'),
      });
    }
    if (showMobileBooking) {
      opts.push({
        id: 'mobile-booking',
        label: 'Mobile Booking',
        description: 'Create a new mobile booking',
        icon: Car,
        onSelect: () => openCreate('mobileBooking'),
      });
    }
    if (isProposalsAppInstalled) {
      opts.push({
        id: 'proposal',
        label: 'Proposal',
        description: 'Create a new proposal',
        icon: FileText,
        onSelect: () => openCreate('proposal'),
      });
    }
    if (showFieldQuotes) {
      opts.push({
        id: 'field-quote',
        label: 'Field Quote',
        description: 'Schedule a field quote for this customer',
        icon: ClipboardList,
        onSelect: () => openCreate('fieldQuote'),
      });
    }
    // Only offer "Create Lead" when CRM is installed and the customer isn't
    // already an open lead (existing leads are surfaced via the Lead banner).
    if (isCrmAppInstalled && customerHasOpenLead === false) {
      opts.push({
        id: 'lead',
        label: 'Create Lead',
        description: 'Add this customer as a new lead',
        icon: Sparkles,
        onSelect: () => { void handleCreateLead(); },
      });
    }
    return opts;
    // openCreate is stable across renders for the purpose of this menu — the customer/business
    // dependencies that actually matter are listed below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customer?.is_supplier,
    customer?.id,
    selectedBusiness?.id,
    isInventoryAppInstalled,
    isProposalsAppInstalled,
    isCrmAppInstalled,
    customerHasOpenLead,
    handleCreateLead,
    showLocationBooking,
    showMobileBooking,
    showFieldQuotes,
  ]);

  if (!customer) return null;

  return (
    <>
      {menuOptions.length > 0 && (
        <CrmCreateActionDropdown
          options={menuOptions}
          align={align}
          ariaLabel="Create"
          menuTitle="Create"
        />
      )}

      {activeCreateType === 'booking' && (
        <CreateBookingModal
          isOpen
          onClose={() => setActiveCreateType(null)}
          onSuccess={() => setActiveCreateType(null)}
          initialCustomer={customer || undefined}
        />
      )}
      {activeCreateType === 'mobileBooking' && (
        <CreateMobileBookingModal
          isOpen
          onClose={() => setActiveCreateType(null)}
          onSuccess={() => setActiveCreateType(null)}
          prefilledData={customer ? { customer } : undefined}
        />
      )}
      {activeCreateType === 'proposal' && (
        <CreateProposalModal
          isOpen
          onClose={() => setActiveCreateType(null)}
          onSuccess={() => setActiveCreateType(null)}
          initialCustomer={customer || undefined}
        />
      )}
      {activeCreateType === 'placeOrder' && (
        <PlaceOrderModal
          isOpen
          onClose={() => setActiveCreateType(null)}
          items={placeOrderItems}
        />
      )}
      {activeCreateType === 'fieldQuote' && (
        <ScheduleFieldQuoteModal
          isOpen
          onClose={() => setActiveCreateType(null)}
          projectId={null}
          projectTitle={null}
          initialCustomer={customer || null}
          onSuccess={() => setActiveCreateType(null)}
        />
      )}
    </>
  );
}
